import React, { useState } from "react";
import { useIntl } from "react-intl";
import ChartSettingsDialog from "../ChartSettingsDialog";
import ChartTabs from "../ChartTabs";
import InputsTab from "./InputsTab";
import StylesTab from "./StylesTab";
import useIndicatorDraft from "../../hooks/useIndicatorDraft";
import { getIndicatorDefinition } from "../../engine/indicators/registry";

const TAB_INPUTS = "inputs";
const TAB_STYLES = "styles";

/**
 * Settings of one indicator (po-chart-visual-spec.md §5.4): the Inputs and
 * Styles tabs in the settings frame. Mount it with `key={indicator.key}`: the
 * edits start from the saved values.
 *
 * @param {object}   props
 * @param {object}   props.indicator - engine.getIndicators() item
 * @param {string[]} props.palette - engine.getLinePalette()
 * @param {(changes: { inputs: object, styles: object }) => void} props.onSave
 * @param {() => void} props.onRemove
 * @param {() => void} props.onClose
 */
const IndicatorSettingsModal = ({ indicator, palette, onSave, onRemove, onClose }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const definition = getIndicatorDefinition(indicator.id);
  const [tab, setTab] = useState(TAB_INPUTS);
  const { draft, setInput, commitInput, setPlot, setLevel, commitLevel, getChanges } = useIndicatorDraft(
    indicator,
    definition,
  );

  return (
    <ChartSettingsDialog
      title={indicator.name}
      onSave={() => onSave(getChanges())}
      onRemove={onRemove}
      onClose={onClose}
    >
      <ChartTabs
        variant="pill"
        tabs={[
          { id: TAB_INPUTS, label: t("futures.chart.indicator_settings.tab_inputs") },
          { id: TAB_STYLES, label: t("futures.chart.indicator_settings.tab_styles") },
        ]}
        activeId={tab}
        onSelect={setTab}
      />
      {tab === TAB_INPUTS ? (
        <InputsTab inputs={definition.inputs} values={draft.inputs} onChange={setInput} onCommit={commitInput} />
      ) : (
        <StylesTab
          plots={definition.plots}
          plotStyles={draft.plots}
          levels={draft.levels}
          palette={palette}
          onPlotChange={setPlot}
          onLevelChange={setLevel}
          onLevelCommit={commitLevel}
        />
      )}
    </ChartSettingsDialog>
  );
};

export default IndicatorSettingsModal;
